/**
 * События мини-аппа для аналитики владельца: свой эндпоинт `POST /api/client/events`.
 *
 * До 30.09.2026 здесь стоял PostHog: внешний скрипт, выключенный без ключа, то есть
 * ни одного события сервер так и не увидел. Теперь всё уходит к нам.
 *
 * Дизайн (не ломать):
 * - Сырой `fetch`, не `api.request()` и не react-query: ошибка отправки не должна
 *   попасть в queryCache.subscribe и превратиться в тост или отчёт об ошибке.
 * - `track` никогда не бросает и ничего не ждёт: очередь в памяти, пачка раз в 5 секунд
 *   или по 10 событий, остаток уходит при сворачивании (`keepalive`).
 * - Пока нет токена, событие ждёт до 15 секунд: без токена сервер не знает клиента.
 *   Потом уходит анонимно с `bot_id` из адреса, чтобы не пропасть совсем.
 * - Промокод и суммы в свойства не кладём: серверу хватает вида события и одного
 *   измерения (`dim`), а лишнее он всё равно выбросит.
 */

import { api, currentBotId } from '@/lib/api';

const API_BASE = import.meta.env.VITE_API_BASE || '';
const ENDPOINT = '/api/client/events';
const USE_MOCK_FALLBACK = import.meta.env.VITE_USE_MOCK_FALLBACK === 'true';

const FLUSH_MS = 5_000;
const FLUSH_AT = 10;
const MAX_BATCH = 25;
const MAX_QUEUE = 200;
const WAIT_TOKEN_MS = 15_000;
const SESSION_KEY = 'pe_sid';

interface QueuedEvent {
  kind: string;
  ts: number;
  dim?: string;
  props?: Record<string, unknown>;
}

const queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let firstQueuedAt = 0;
let started = false;

function sessionId(): string {
  try {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) return saved;
    const fresh = (globalThis.crypto ?? window.crypto).randomUUID();
    sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return 'nosession';
  }
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

/** Одно измерение с малым числом значений: по нему сервер и режет воронки. */
function dimFor(event: string, props: Record<string, unknown>): string | undefined {
  switch (event) {
    case 'plan_selected':
      if (props.lifetime) return 'lifetime';
      return typeof props.days === 'number' ? String(props.days) : undefined;
    case 'payment_initiated':
      return str(props.provider);
    case 'payment_succeeded':
      return str(props.intent_type);
    case 'promo_applied':
      return str(props.discount_type);
    case 'promo_failed':
      return str(props.code);
    case 'screen_view':
      return str(props.route);
    default:
      return undefined;
  }
}

/** Свойства, которые сервер принимает. Остальное не отправляем вовсе. */
function propsFor(
  event: string,
  props: Record<string, unknown>,
): Record<string, unknown> | undefined {
  switch (event) {
    case 'plans_viewed':
      return { count: props.count };
    case 'plan_selected':
    case 'payment_initiated':
      return { plan_id: props.plan_id, price_rub: props.price_rub };
    case 'checkout_opened':
      return { plan_id: props.plan_id };
    case 'promo_applied':
      return { discount_type: props.discount_type };
    case 'app_opened':
      return { platform: props.platform };
    default:
      return undefined;
  }
}

function schedule(): void {
  if (timer !== null) return;
  timer = setTimeout(() => {
    timer = null;
    flush(false);
  }, FLUSH_MS);
}

function flush(final: boolean): void {
  if (!queue.length) return;
  const token = api.getAuth().token;
  if (!token && !final && Date.now() - firstQueuedAt < WAIT_TOKEN_MS) {
    schedule();
    return;
  }
  const batch = queue.splice(0, MAX_BATCH);
  firstQueuedAt = queue.length ? Date.now() : 0;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    void fetch(`${API_BASE}${ENDPOINT}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        surface: 'miniapp',
        session_id: sessionId(),
        bot_id: token ? undefined : (currentBotId() ?? undefined),
        events: batch,
      }),
      keepalive: true,
    }).catch(() => {
      /* аналитика best-effort */
    });
  } catch {
    /* аналитика best-effort */
  }
  if (queue.length) schedule();
}

/** Подписки на сворачивание и закрытие. Один раз на запуск. */
export function initAnalytics(): void {
  if (started || USE_MOCK_FALLBACK) return;
  started = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
  window.addEventListener('pagehide', () => flush(true));
}

export function track(event: string, props: Record<string, unknown> = {}): void {
  if (USE_MOCK_FALLBACK) return;
  try {
    if (queue.length >= MAX_QUEUE) queue.shift();
    if (!queue.length) firstQueuedAt = Date.now();
    queue.push({
      kind: event,
      ts: Date.now(),
      dim: dimFor(event, props),
      props: propsFor(event, props),
    });
    if (queue.length >= FLUSH_AT) flush(false);
    else schedule();
  } catch {
    /* аналитика best-effort */
  }
}
